import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormHelperText,
  Input,
  FormControl,
  InputLabel,
  withStyles,
} from '@material-ui/core';
import { compose } from 'redux';
import { translate as translateHOC } from 'react-admin';
import utils from '../../../shared/utils';

const styles = () => ({
  root: {
    flex: 1,
  },
  form: {
    width: '100%',
  },
  formControl: {
    width: '100%',
    marginBottom: 16,
  },
  fullWidth: {
    width: '100%',
  },
  overflowVisible: {
    overflowY: 'visible',
  },
  dialogTitle: {
    padding: '30px 40px 30px 40px',
  },
  dialogContent: {
    padding: '0 40px',
    overflowY: 'visible',
  },
  dialogSubtitle: {
    paddingBottom: 20,
  },
});

const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

const MOP_MAX_LENGTH = 251;
const NOTES_MAX_LENGTH = 255;

const SetAsPaid = ({ classes, open, onClose, setAsPaid, translate }) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState(null);
  const [formIsValid, setFormIsValid] = useState(true);

  const { userId: requesterId } = utils.getCurrentUserDataLogged();

  useEffect(() => {
    const paymentMethodIsTooShort = paymentMethod.length <= 0;
    const paymentMethodIsTooLong = paymentMethod.length > MOP_MAX_LENGTH;
    const notesAreValid = notes.length <= NOTES_MAX_LENGTH;

    setPaymentMethodError(
      paymentMethodIsTooLong
        ? translate('validation.maxLength', {
            max: MOP_MAX_LENGTH,
          })
        : null
    );
    setNotesError(
      notesAreValid
        ? null
        : translate('validation.maxLength', {
            max: NOTES_MAX_LENGTH,
          })
    );
    setFormIsValid(!paymentMethodIsTooShort && !paymentMethodIsTooLong && notesAreValid);
  }, [paymentMethod, notes]);

  const handleSubmit = useCallback(() => {
    if (formIsValid) {
      setAsPaid({ paymentMethod, notes, requesterId });
      onClose();
    }
  }, [formIsValid, paymentMethod, notes, requesterId, setAsPaid, onClose]);

  const resetForm = useCallback(() => {
    setPaymentMethod('');
    setNotes('');
    setPaymentMethodError(null);
    setNotesError(null);
    setFormIsValid(true);
  }, []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableBackdropClick onExited={resetForm}>
      <DialogTitle>{capitalize(translate('resources.invoice.setAsPaid') || '')}</DialogTitle>
      <DialogContent>
        <FormControl className={classes.formControl} error={paymentMethodError}>
          <InputLabel htmlFor="Selenium-FormUserIdentity-ExternalPayment-PaymentMethod">
            {translate('resources.invoice.setAsPaidDialog.paymentMethodLabel')}
          </InputLabel>
          <Input
            id="Selenium-FormUserIdentity-ExternalPayment-PaymentMethod"
            type="string"
            onChange={event => {
              setPaymentMethod(event.target.value);
            }}
            value={paymentMethod}
            isrequired="true"
          />
          {paymentMethodError && <FormHelperText>{paymentMethodError}</FormHelperText>}
        </FormControl>
        <FormControl className={classes.formControl} error={notesError}>
          <InputLabel htmlFor="Selenium-FormUserIdentity-ExternalPayment-Notes">
            {translate('resources.invoice.setAsPaidDialog.notesLabel')}
          </InputLabel>
          <Input
            id="Selenium-FormUserIdentity-ExternalPayment-Notes"
            type="string"
            onChange={event => {
              setNotes(event.target.value);
            }}
            value={notes}
            multiline
          />
          {notesError && <FormHelperText>{notesError}</FormHelperText>}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button id="Selenium-dialog-Users-ExternalPayment-cancel" onClick={onClose} color="primary" variant="outlined">
          {translate('common.cancel')}
        </Button>
        <Button
          id="Selenium-dialog-Users-ExternalPayment-confirm"
          onClick={handleSubmit}
          color="primary"
          disabled={!formIsValid}
          variant="contained"
        >
          {translate('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SetAsPaid.propTypes = {
  classes: PropTypes.shape({}),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  setAsPaid: PropTypes.func,
  translate: PropTypes.func,
};

export default compose(
  translateHOC,
  withStyles(styles)
)(SetAsPaid);
